import { clamp, type V2 } from '../core/math';
import { Layer } from '../physics/types';
import type { PhysicsWorld } from '../physics/world';
import type { Lane, RoadNet } from '../traffic/roadNet';
import type { Vehicle, VehicleControls } from '../vehicles/types';

/**
 * Police driving: a route follower over the RoadNet (for getting somewhere without line of sight)
 * plus direct pursuit steering with predictive intercept, PIT/ram aim points and static-geometry
 * avoidance. Outputs VehicleControls only - police cars obey the same physics as everyone else, can
 * be outrun, crash and be wrecked.
 */

/**
 * How much dearer an alley is than a road to a police route (traffic uses 3). A saloon with a crew in a
 * 2.4 m-wide dead end is the commonest way a unit got stuck for good (docs/POLICE-AI-POLISH.md #1): only a
 * goal INSIDE the alley is worth going in for.
 */
export const POLICE_ALLEY_COST = 12;

export class PathFollower {
  pts: V2[] = [];
  cum: number[] = [];
  goal: V2 = { x: 0, z: 0 };
  private idx = 0;
  /** Sim time the plan was made. */
  plannedAt = -99;
  alleyCost = POLICE_ALLEY_COST;

  get valid(): boolean {
    return this.pts.length >= 2;
  }

  clear(): void {
    this.pts.length = 0;
    this.cum.length = 0;
    this.idx = 0;
  }

  /** Plan along lanes from the car's position to `goal`. Returns false if no route was found. */
  plan(net: RoadNet, from: V2, heading: number, goal: V2, now: number): boolean {
    this.clear();
    this.plannedAt = now;
    this.goal = { x: goal.x, z: goal.z };
    const start = net.nearestLane(from, heading) ?? net.nearestLane(from);
    if (!start) return false;
    const goalNode = nearestNode(net, goal);
    const route = net.findRoute(start.lane, goalNode, this.alleyCost) ?? [];
    this.push(from);
    let lane = start.lane;
    // Remaining part of the current lane.
    this.push(lane.b);
    for (const next of route) {
      const c = net.connector(lane, next);
      for (let i = 1; i < c.pts.length; i++) this.push(c.pts[i]!);
      this.push(next.b);
      lane = next;
    }
    this.push(goal);
    return this.valid;
  }

  private push(p: V2): void {
    const last = this.pts[this.pts.length - 1];
    if (last && Math.hypot(p.x - last.x, p.z - last.z) < 0.5) return;
    this.cum.push(last ? this.cum[this.cum.length - 1]! + Math.hypot(p.x - last.x, p.z - last.z) : 0);
    this.pts.push({ x: p.x, z: p.z });
  }

  /** Remaining distance along the path from the closest point to `pos`. */
  remaining(pos: V2): number {
    const s = this.project(pos);
    return this.cum[this.cum.length - 1]! - s;
  }

  /** Point `ahead` metres further along the path than the projection of `pos`. */
  lookahead(pos: V2, ahead: number, out: V2): V2 {
    const s = this.project(pos) + ahead;
    const n = this.pts.length;
    if (s >= this.cum[n - 1]!) {
      out.x = this.pts[n - 1]!.x;
      out.z = this.pts[n - 1]!.z;
      return out;
    }
    let i = this.idx;
    while (i < n - 2 && this.cum[i + 1]! < s) i++;
    const seg = this.cum[i + 1]! - this.cum[i]! || 1;
    const u = clamp((s - this.cum[i]!) / seg, 0, 1);
    out.x = this.pts[i]!.x + (this.pts[i + 1]!.x - this.pts[i]!.x) * u;
    out.z = this.pts[i]!.z + (this.pts[i + 1]!.z - this.pts[i]!.z) * u;
    return out;
  }

  private project(pos: V2): number {
    const n = this.pts.length;
    let best = this.idx;
    let bestD = Infinity;
    let bestS = this.cum[this.idx] ?? 0;
    const last = Math.min(n - 2, this.idx + 6);
    for (let i = this.idx; i <= last; i++) {
      const a = this.pts[i]!;
      const b = this.pts[i + 1]!;
      const ex = b.x - a.x;
      const ez = b.z - a.z;
      const l2 = ex * ex + ez * ez || 1;
      const t = clamp(((pos.x - a.x) * ex + (pos.z - a.z) * ez) / l2, 0, 1);
      const d = Math.hypot(a.x + ex * t - pos.x, a.z + ez * t - pos.z);
      if (d < bestD) {
        bestD = d;
        best = i;
        bestS = this.cum[i]! + Math.sqrt(l2) * t;
      }
    }
    this.idx = best;
    return bestS;
  }
}

export function nearestNode(net: RoadNet, p: V2): number {
  let best = 0;
  let bestD = Infinity;
  for (const n of net.layout.roads.nodes) {
    const d = (n.pos.x - p.x) ** 2 + (n.pos.z - p.z) ** 2;
    if (d < bestD) {
      bestD = d;
      best = n.id;
    }
  }
  return best;
}

export interface SteerResult {
  /** Distance to the aim point. */
  dist: number;
  /** Signed angle to the aim point (rad, + = right). */
  angle: number;
  /** Static obstacle straight ahead closer than this (m), Infinity if clear. */
  blockedAt: number;
}


/**
 * Pure-pursuit steering towards `aim` and a speed controller towards `desired` (m/s), with corner
 * slowing and wall avoidance via two short raycasts against static geometry.
 */
export function driveToward(
  v: Vehicle,
  aim: V2,
  desired: number,
  physics: PhysicsWorld,
  out: VehicleControls,
  opts: { reverseOk?: boolean; avoidWalls?: boolean } = {},
): SteerResult {
  const spec = v.spec;
  const sinH = Math.sin(v.heading);
  const cosH = Math.cos(v.heading);
  const vF = v.velocity.x * sinH + v.velocity.z * cosH;
  const dx = aim.x - v.pos.x;
  const dz = aim.z - v.pos.z;
  const dist = Math.hypot(dx, dz);
  const lz = dx * sinH + dz * cosH;
  const lx = -dx * cosH + dz * sinH; // + = right
  const angle = Math.atan2(lx, lz);

  // Walls: look ahead along the heading, nudge away from whatever is closer.
  let blockedAt = Infinity;
  let avoid = 0;
  if (opts.avoidWalls !== false && Math.abs(vF) > 2) {
    const reach = 5 + Math.abs(vF) * 0.9;
    const origin = { x: v.pos.x + sinH * (spec.length / 2), z: v.pos.z + cosH * (spec.length / 2) };
    const c = physics.raycast(origin, { x: sinH, z: cosH }, reach, { mask: 0 });
    if (c && c.distance < reach) {
      blockedAt = c.distance;
      const yawL = v.heading + 0.45;
      const yawR = v.heading - 0.45;
      const l = physics.raycast(origin, { x: Math.sin(yawL), z: Math.cos(yawL) }, reach, { mask: 0 });
      const r = physics.raycast(origin, { x: Math.sin(yawR), z: Math.cos(yawR) }, reach, { mask: 0 });
      const freeL = l ? l.distance : reach;
      const freeR = r ? r.distance : reach;
      // Positive heading change = left; steer +1 = right.
      avoid = freeR > freeL ? 0.7 : -0.7;
      // Only override if the aim point is not clearly to one side already.
      if (Math.abs(angle) > 0.6) avoid *= 0.3;
    }
  }

  // Pure pursuit on the aim point (bicycle model), then add avoidance.
  const ld = Math.max(dist, 3);
  const delta = Math.atan((2 * spec.wheelbase * Math.sin(angle)) / ld);
  const speed = Math.abs(vF);
  const authority = Math.max(spec.steerAngle / (1 + (speed / spec.steerFalloffSpeed) ** 2), spec.minSteer ?? 0.15);
  let steer = clamp(delta / authority + avoid, -1, 1);

  // Corner slowing: the sharper the aim angle, the slower we may go.
  const sharp = Math.abs(angle);
  let target = desired;
  if (sharp > 0.35) target = Math.min(target, Math.max(7, 30 * (1 - sharp / 1.9)));
  if (blockedAt < Infinity) target = Math.min(target, Math.max(3, Math.sqrt(2 * 7 * Math.max(0, blockedAt - 1.5))));

  let throttle: number;
  let handbrake = false;
  if (lz < -2 && dist < 25 && opts.reverseOk && speed < 4) {
    // Target is right behind us: reverse towards it.
    throttle = -0.8;
    steer = -clamp(lx / 6, -1, 1);
  } else {
    const err = target - vF;
    throttle = err > 0 ? clamp(err * 0.5, 0.15, 1) : clamp(err * 0.6, -1, 0);
    if (target < 0.5 && Math.abs(vF) < 1) {
      throttle = 0;
      handbrake = true;
    }
    // A fast, sharp turn: a dab of handbrake swings the tail round (police training!).
    if (sharp > 1.3 && speed > 9 && speed < 22) handbrake = true;
  }
  out.throttle = throttle;
  out.steer = steer;
  out.handbrake = handbrake;
  if (throttle !== 0 && v.body.sleeping) v.body.wake();
  return { dist, angle, blockedAt };
}

/** Where a pursuer should aim for a given tactic. */
export function tacticAim(tactic: 'chase' | 'intercept' | 'ram' | 'box', target: { pos: V2; vel: V2; heading: number }, from: V2, side: number, out: V2): V2 {
  const fx = Math.sin(target.heading);
  const fz = Math.cos(target.heading);
  const rx = -fz;
  const rz = fx;
  const d = Math.hypot(target.pos.x - from.x, target.pos.z - from.z);
  switch (tactic) {
    case 'intercept': {
      const t = clamp(d / 18, 0.4, 2.2);
      out.x = target.pos.x + target.vel.x * t;
      out.z = target.pos.z + target.vel.z * t;
      return out;
    }
    case 'ram': {
      // PIT: aim at the rear quarter on our side, slightly ahead of where it will be.
      const t = clamp(d / 30, 0.1, 0.6);
      out.x = target.pos.x - fx * 1.3 + rx * side * 0.9 + target.vel.x * t;
      out.z = target.pos.z - fz * 1.3 + rz * side * 0.9 + target.vel.z * t;
      return out;
    }
    case 'box': {
      // Get alongside / ahead to hem it in.
      const t = clamp(d / 25, 0.3, 1.2);
      out.x = target.pos.x + fx * 6 + rx * side * 2.6 + target.vel.x * t;
      out.z = target.pos.z + fz * 6 + rz * side * 2.6 + target.vel.z * t;
      return out;
    }
    default: {
      const t = clamp(d / 40, 0, 0.8);
      out.x = target.pos.x + target.vel.x * t;
      out.z = target.pos.z + target.vel.z * t;
      return out;
    }
  }
}

/**
 * Getting unstuck (docs/POLICE-AI-POLISH.md #1). A unit that asks for power and gets nowhere reverses with a
 * counter-steer; stuck AGAIN near the same spot within STUCK_MEMORY seconds it reverses longer, swings the
 * other way, and then dodges sideways for a while instead of driving straight back into the same post, wall
 * or car. Pure, shared with the server-run units.
 */
export interface StuckMemory {
  at: V2 | null;
  time: number;
  count: number;
}
export const STUCK_MEMORY = 14;
export const STUCK_SAME_SPOT = 8;
/** Lateral offset (m) of the aim point while dodging after a recovery. */
export const DODGE_OFFSET = 3.5;

export interface Recovery {
  /** Seconds of reversing. */
  reverse: number;
  /** Which way the nose should swing (+1 = right); steer the opposite way while reversing. */
  side: number;
  /** Seconds to keep the aim point DODGE_OFFSET to `side` afterwards. */
  dodge: number;
}

/**
 * `openLeft` / `openRight`: is there room a car length ahead swung to that side (a shape test)? `aimAngle`: the
 * signed angle to the aim point (+ = right), the tie-break.
 */
export function planRecovery(m: StuckMemory, pos: V2, now: number, openLeft: boolean, openRight: boolean, aimAngle: number): Recovery {
  const again = m.at !== null && Math.hypot(m.at.x - pos.x, m.at.z - pos.z) < STUCK_SAME_SPOT && now - m.time < STUCK_MEMORY;
  m.count = again ? m.count + 1 : 1;
  m.at = { x: pos.x, z: pos.z };
  m.time = now;
  let side = openRight && !openLeft ? 1 : openLeft && !openRight ? -1 : aimAngle > 0 ? 1 : -1;
  // Never the same move twice: the second attempt at a spot goes the other way.
  if (m.count % 2 === 0) side = -side;
  return { reverse: m.count >= 2 ? 2.2 : 1.1, side, dodge: 2 + Math.min(m.count, 4) * 0.5 };
}

export type PursuitTactic = 'chase' | 'intercept' | 'ram' | 'box';

/** A rammer only goes for the PIT when within this angle (rad) of the suspect's heading: from behind, never across. */
export const RAM_MAX_ANGLE = 0.6;
/** Pursuers are re-ranked by distance only this often (s): tactics stick instead of flipping every step. */
export const TACTIC_RERANK = 2.5;

/** The tactic for the `rank`-th closest pursuer at a wanted level (docs/POLICE.md §3). */
export function tacticForRank(level: number, rank: number): PursuitTactic {
  if (level >= 4) return rank === 0 ? 'ram' : rank === 1 ? 'box' : rank % 2 === 0 ? 'ram' : 'intercept';
  if (level >= 3) return rank === 0 ? 'ram' : rank === 1 ? 'intercept' : 'chase';
  if (level >= 2) return rank === 1 ? 'intercept' : 'chase';
  return 'chase';
}

/**
 * Which side of the suspect each ranked pursuer works (+1 = the suspect's right): the side it is actually
 * on, except that no two consecutive ranks share one, so the rammer and the boxer make a pincer instead
 * of a queue on the same rear quarter (docs/POLICE-AI-POLISH.md #3). `lateral`: signed offset of each
 * pursuer across the suspect's heading, in rank order.
 */
export function sidesForRanks(lateral: readonly number[]): number[] {
  const out: number[] = [];
  for (let i = 0; i < lateral.length; i++) {
    let side = lateral[i]! >= 0 ? 1 : -1;
    if (i > 0 && out[i - 1] === side) side = -side;
    out.push(side);
  }
  return out;
}

/**
 * Where a searching unit should look next (docs/POLICE-AI-POLISH.md #4): a junction inside the search
 * circle, preferring the way the suspect was last seen going (`dir`, null when unknown), not one another
 * searcher is already heading for (`taken`), not the one it is standing at, with a little randomness so
 * the sweep spreads. `nodes` are road junctions (callers leave alley-only nodes out).
 */
export function pickSearchGoal(nodes: readonly { pos: V2 }[], center: V2, radius: number, dir: V2 | null, taken: readonly V2[], from: V2, rnd: () => number): V2 | null {
  const dl = dir ? Math.hypot(dir.x, dir.z) : 0;
  const dx = dl > 2 ? dir!.x / dl : 0;
  const dz = dl > 2 ? dir!.z / dl : 0;
  let best: V2 | null = null;
  let bestW = -Infinity;
  let nearest: V2 | null = null;
  let nearestD = Infinity;
  for (const n of nodes) {
    const cx = n.pos.x - center.x;
    const cz = n.pos.z - center.z;
    const dc = Math.hypot(cx, cz);
    if (dc < nearestD) {
      nearestD = dc;
      nearest = n.pos;
    }
    if (dc > radius) continue;
    let w = 1;
    if (dl > 2 && dc > 1) w *= 1 + 0.9 * ((cx * dx + cz * dz) / dc);
    for (const t of taken) if (Math.hypot(t.x - n.pos.x, t.z - n.pos.z) < 25) w *= 0.2;
    if (Math.hypot(from.x - n.pos.x, from.z - n.pos.z) < 12) w *= 0.3;
    w *= 0.6 + 0.8 * rnd();
    if (w > bestW) {
      bestW = w;
      best = n.pos;
    }
  }
  return best ?? nearest;
}

/** Seconds a roadblock the suspect is plainly driving away from (far, and heading away) stays up before it joins the chase. */
export const ROADBLOCK_MISS_S = 4;
/** A roadblock goes up at least this far ahead of the suspect (m), and never on screen. */
export const ROADBLOCK_MIN_AHEAD = 60;
export const ROADBLOCK_MAX_AHEAD = 85;

/**
 * Where to throw a roadblock across the suspect's path (docs/POLICE-AI-POLISH.md #2). On the road they are
 * on if there is room ahead of them (they must come past it or turn back), otherwise just beyond the next
 * junction on the straight-on exit — never six lanes away on a guess. Returns the lane and the distance
 * along it, or null when nothing suits.
 */
export function roadblockSite(net: RoadNet, pos: V2, heading: number, minAhead = ROADBLOCK_MIN_AHEAD, maxAhead = ROADBLOCK_MAX_AHEAD): { lane: Lane; along: number } | null {
  const hit = net.nearestLane(pos, heading) ?? net.nearestLane(pos);
  if (!hit) return null;
  let lane = hit.lane;
  const remaining = lane.length - hit.s;
  if (!lane.alley && remaining >= minAhead + 6) return { lane, along: hit.s + clamp(remaining - 6, minAhead, maxAhead) };
  // Straight on through the next junctions (lanes can be short: a long road is many of them) until the
  // distance is made; the block goes up just past a junction, or further along when that is what it takes.
  let acc = remaining;
  for (let i = 0; i < 8; i++) {
    const succ = net.successors(lane);
    if (succ.length === 0) return null;
    let best = succ[0]!;
    let bestDot = -2;
    for (const m of succ) {
      const d = m.dir.x * lane.dir.x + m.dir.z * lane.dir.z;
      if (d > bestDot) {
        bestDot = d;
        best = m;
      }
    }
    lane = best;
    const along = Math.max(Math.min(14, lane.length * 0.3), minAhead - acc);
    if (!lane.alley && lane.length >= 12 && along <= lane.length - 6) return { lane, along };
    acc += lane.length;
    if (acc > maxAhead + 40) return null;
  }
  return null;
}

/** Is there a civilian vehicle right ahead (so a sane copper eases off)? */
/** Free distance ahead (m) along the heading; `lateral` shifts the probe sideways (+ = left). */
export function vehicleAhead(v: Vehicle, physics: PhysicsWorld, ignoreBodyId: number | null, reach: number, lateral = 0): number {
  const sinH = Math.sin(v.heading);
  const cosH = Math.cos(v.heading);
  const rx = -cosH;
  const rz = sinH;
  const ignore = ignoreBodyId !== null ? { id: ignoreBodyId } : null;
  let best = Infinity;
  // Centre and both front corners: an angled, half-parked car is caught by a corner ray.
  for (const k of [0, -0.85, 0.85]) {
    const off = (v.spec.width / 2) * k - lateral;
    const origin = { x: v.pos.x + sinH * (v.spec.length / 2 + 0.2) + rx * off, z: v.pos.z + cosH * (v.spec.length / 2 + 0.2) + rz * off };
    const hit = physics.raycast(origin, { x: sinH, z: cosH }, reach, { mask: Layer.VEHICLE, includeStatics: false, ignore });
    if (hit && hit.distance < best) best = hit.distance;
  }
  return best;
}

